/**
 * Capacity is measured in **seats**, not RSVP rows.
 *
 * An attendee bringing two guests occupies three seats, so a plain attendee
 * count under-reports a full event. These helpers are the single place that
 * arithmetic lives, so the join decision and the "is it full" badge cannot
 * disagree.
 */

export type SeatState = Readonly<{
  /** Seats already taken — attendees plus their guests. */
  seatsTaken: number;
  /** `null`/`undefined`/0 means unlimited. */
  capacity?: number | null;
  isRegistrationRequired: boolean;
}>;

/** True when no further seats are available. */
export const isEventFull = ({
  seatsTaken,
  capacity,
  isRegistrationRequired,
}: SeatState): boolean =>
  isRegistrationRequired && !!capacity && seatsTaken >= capacity;

/**
 * Whether a party of `1 + guestCount` still fits.
 *
 * Checking "is the event full" alone is not enough: with one seat left, a user
 * bringing two guests would slip in and overflow the event.
 */
export const partyFits = (
  state: SeatState,
  guestCount: number,
): boolean => {
  if (!state.isRegistrationRequired || !state.capacity) return true;
  return state.seatsTaken + 1 + Math.max(0, guestCount) <= state.capacity;
};
